import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Trash2, PackageX } from "lucide-react";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { useFavorites } from "../../../context/FavoriteContext/FavoriteContext";
import { useCart } from "../../../context/CartContext/CartContext";
import axiosInstance from "../../../api/axiosInstance";

type FavoriUrun = {
  favoriId: number;
  urunId: number;
  ad: string;
  fiyat: number;
  indirimliFiyat?: number | null;
  indirimAktifMi?: boolean | null;
  paraBirimi: string;
  fotoMain?: string | null;
  stokAdedi?: number | null;
  aktifMi: boolean;
  eklemeTarihi: string;
};

function money(v: number, currency: string) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(v);
}

const FavoritesPage: React.FC = () => {
  const { customer, token } = useCustomerAuth();
  const { toggle, reload } = useFavorites();
  const { open: openCart } = useCart();

  const [items, setItems] = useState<FavoriUrun[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<Set<number>>(new Set());

  const musteriId = customer?.id;

  const load = async () => {
    if (!musteriId || !token) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post("/Favoriler/listele", { musteriId });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setItems(res.data.data);
      }
    } catch {
      /* yoksay */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [musteriId, token]);

  const handleRemove = async (urunId: number) => {
    setRemoving((prev) => new Set(prev).add(urunId));
    await toggle(urunId);
    setItems((prev) => prev.filter((x) => x.urunId !== urunId));
    setRemoving((prev) => {
      const s = new Set(prev);
      s.delete(urunId);
      return s;
    });
    reload();
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 mb-2">
          <Heart size={20} className="text-red-400" />
          <h2 className="text-lg font-bold text-slate-800">Favorilerim</h2>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
        ))}
      </div>
    );
  }

  /* ── Boş durum ── */
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-5 py-16">
        <div className="w-20 h-20 rounded-full bg-red-50 grid place-items-center">
          <Heart size={36} className="text-red-300" />
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-slate-800 mb-1">
            Favori ürününüz yok
          </div>
          <div className="text-sm text-slate-500">
            Beğendiğiniz ürünlerin kalbine tıklayarak buraya ekleyebilirsiniz.
          </div>
        </div>
        <Link
          to="/products"
          className="h-11 px-6 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition inline-flex items-center gap-2"
        >
          Ürünleri Keşfet →
        </Link>
      </div>
    );
  }

  /* ── Liste ── */
  return (
    <div className="flex flex-col gap-2">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Heart size={20} className="fill-red-500 text-red-500" />
          <h2 className="text-lg font-bold text-slate-800">Favorilerim</h2>
          <span className="text-xs font-semibold bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
            {items.length}
          </span>
        </div>
      </div>

      {/* Ürün listesi */}
      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const currency = item.paraBirimi || "TRY";
          const isDiscount =
            (item.indirimAktifMi ?? true) &&
            item.indirimliFiyat != null &&
            item.indirimliFiyat > 0 &&
            item.indirimliFiyat < item.fiyat;
          const salePrice = isDiscount ? item.indirimliFiyat! : item.fiyat;
          const outOfStock =
            typeof item.stokAdedi === "number" ? item.stokAdedi <= 0 : false;
          const isRemoving = removing.has(item.urunId);

          return (
            <div
              key={item.favoriId}
              className={[
                "flex items-center gap-4 p-4 rounded-2xl border bg-white",
                "shadow-[0_1px_4px_rgba(15,23,42,0.06)] transition-all duration-200",
                isRemoving ? "opacity-40 scale-[0.99]" : "",
              ].join(" ")}
            >
              {/* Görsel */}
              <Link
                to={`/urun/${item.urunId}`}
                className="shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-slate-100 bg-slate-50"
              >
                {item.fotoMain ? (
                  <img
                    src={item.fotoMain}
                    alt={item.ad}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-300">
                    <PackageX size={24} />
                  </div>
                )}
              </Link>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/urun/${item.urunId}`}
                  className="text-sm font-semibold text-slate-800 hover:text-blue-600 transition-colors line-clamp-2 leading-snug"
                >
                  {item.ad}
                </Link>

                <div className="mt-1.5 flex items-baseline gap-2">
                  <span className="text-base font-bold text-slate-900">
                    {money(salePrice, currency)}
                  </span>
                  {isDiscount && (
                    <span className="text-xs text-slate-400 line-through">
                      {money(item.fiyat, currency)}
                    </span>
                  )}
                </div>

                {outOfStock && (
                  <span className="mt-1 inline-block text-[11px] font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                    Stokta yok
                  </span>
                )}
              </div>

              {/* Aksiyonlar */}
              <div className="shrink-0 flex flex-col gap-2">
                <Link
                  to={`/urun/${item.urunId}`}
                  className="h-9 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition inline-flex items-center gap-1.5"
                >
                  <ShoppingCart size={13} />
                  İncele
                </Link>
                <button
                  onClick={() => handleRemove(item.urunId)}
                  disabled={isRemoving}
                  className="h-9 px-3 rounded-xl text-xs font-semibold text-red-500 border border-red-100 hover:bg-red-50 transition inline-flex items-center gap-1.5"
                >
                  <Trash2 size={13} />
                  Kaldır
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FavoritesPage;
